import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    mode: "light",

    primary: {
      main: "#008B8B",
      light: "#00A6A6",
      dark: "#006F6F",
      contrastText: "#FFFFFF",
    },

    secondary: {
      main: "#333333",
      light: "#4A4A4A",
      dark: "#222222",
      contrastText: "#FFFFFF",
    },

    background: {
      default: "#F6F8F9",
      paper: "#FFFFFF",
    },

    text: {
      primary: "#333333",
      secondary: "#667085",
      disabled: "#98A2B3",
    },

    divider: "#E4E7EC",

    success: {
      main: "#12B76A",
      light: "#ECFDF3",
      dark: "#027A48",
    },

    warning: {
      main: "#F79009",
      light: "#FFFAEB",
      dark: "#B54708",
    },

    error: {
      main: "#F04438",
      light: "#FEF3F2",
      dark: "#B42318",
    },

    info: {
      main: "#2E90FA",
      light: "#EFF8FF",
      dark: "#175CD3",
    },
  },

  typography: {
    fontFamily: [
      "Inter",
      "Poppins",
      "Roboto",
      "Arial",
      "sans-serif",
    ].join(","),

    h1: {
      fontSize: "2rem",
      fontWeight: 700,
      color: "#333333",
    },

    h2: {
      fontSize: "1.75rem",
      fontWeight: 700,
      color: "#333333",
    },

    h3: {
      fontSize: "1.5rem",
      fontWeight: 700,
      color: "#333333",
    },

    h4: {
      fontSize: "1.35rem",
      fontWeight: 700,
      color: "#333333",
    },

    h5: {
      fontSize: "1.15rem",
      fontWeight: 700,
      color: "#333333",
    },

    h6: {
      fontSize: "1rem",
      fontWeight: 600,
      color: "#333333",
    },

    body1: {
      fontSize: "0.9rem",
      color: "#344054",
    },

    body2: {
      fontSize: "0.825rem",
      color: "#667085",
    },

    button: {
      fontSize: "0.875rem",
      fontWeight: 600,
      textTransform: "none",
    },
  },

  shape: {
    borderRadius: 10,
  },

  shadows: [
    "none",
    "0 1px 2px rgba(16, 24, 40, 0.05)",
    "0 2px 4px rgba(16, 24, 40, 0.06)",
    "0 4px 8px rgba(16, 24, 40, 0.06)",
    "0 6px 12px rgba(16, 24, 40, 0.06)",
    "0 8px 16px rgba(16, 24, 40, 0.07)",
    "0 10px 20px rgba(16, 24, 40, 0.07)",
    "0 12px 24px rgba(16, 24, 40, 0.08)",
    "0 16px 32px rgba(16, 24, 40, 0.08)",
    "0 20px 40px rgba(16, 24, 40, 0.08)",
    "0 24px 48px rgba(16, 24, 40, 0.09)",
    "0 28px 56px rgba(16, 24, 40, 0.09)",
    "0 32px 64px rgba(16, 24, 40, 0.1)",
    "0 36px 72px rgba(16, 24, 40, 0.1)",
    "0 40px 80px rgba(16, 24, 40, 0.1)",
    "0 44px 88px rgba(16, 24, 40, 0.1)",
    "0 48px 96px rgba(16, 24, 40, 0.1)",
    "0 52px 104px rgba(16, 24, 40, 0.1)",
    "0 56px 112px rgba(16, 24, 40, 0.1)",
    "0 60px 120px rgba(16, 24, 40, 0.1)",
    "0 64px 128px rgba(16, 24, 40, 0.1)",
    "0 68px 136px rgba(16, 24, 40, 0.1)",
    "0 72px 144px rgba(16, 24, 40, 0.1)",
    "0 76px 152px rgba(16, 24, 40, 0.1)",
    "0 80px 160px rgba(16, 24, 40, 0.1)",
  ],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          padding: 0,
          backgroundColor: "#F6F8F9",
          color: "#333333",
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },

        "*": {
          boxSizing: "border-box",
        },

        "*::-webkit-scrollbar": {
          width: "6px",
          height: "6px",
        },

        "*::-webkit-scrollbar-track": {
          background: "#F2F4F7",
        },

        "*::-webkit-scrollbar-thumb": {
          background: "#CBD5D5",
          borderRadius: "10px",
        },

        "*::-webkit-scrollbar-thumb:hover": {
          background: "#98A2A2",
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 40,
          padding: "8px 16px",
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
          boxShadow: "none",

          "&:hover": {
            boxShadow: "none",
          },
        },

        containedPrimary: {
          backgroundColor: "#008B8B",

          "&:hover": {
            backgroundColor: "#006F6F",
          },
        },

        outlinedPrimary: {
          borderColor: "#008B8B",
          color: "#008B8B",

          "&:hover": {
            borderColor: "#006F6F",
            backgroundColor: "rgba(0, 139, 139, 0.05)",
          },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: "1px solid #E4E7EC",
          boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
          backgroundColor: "#FFFFFF",
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },

    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: 8,
          backgroundColor: "#FFFFFF",

          "& fieldset": {
            borderColor: "#D0D5DD",
          },

          "&:hover fieldset": {
            borderColor: "#98A2B3",
          },

          "&.Mui-focused fieldset": {
            borderColor: "#008B8B",
            borderWidth: 1,
          },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          color: "#667085",

          "&.Mui-focused": {
            color: "#008B8B",
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: "0.875rem",
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid #EAECF0",
          padding: "12px 16px",
          fontSize: "0.825rem",
        },

        head: {
          backgroundColor: "#F9FAFB",
          color: "#475467",
          fontWeight: 600,
          whiteSpace: "nowrap",
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "#F9FAFB",
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          fontSize: "0.75rem",
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          boxShadow: "0 20px 40px rgba(16, 24, 40, 0.15)",
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1.1rem",
          fontWeight: 700,
          color: "#333333",
          padding: "20px 24px 12px",
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "12px 24px 20px",
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#FFFFFF",
          borderRight: "1px solid #E4E7EC",
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,

          "&.Mui-selected": {
            backgroundColor: "rgba(0, 139, 139, 0.08)",
            color: "#008B8B",

            "&:hover": {
              backgroundColor: "rgba(0, 139, 139, 0.12)",
            },
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#333333",
          fontSize: "0.75rem",
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#667085",

          "&:hover": {
            backgroundColor: "rgba(0, 139, 139, 0.06)",
            color: "#008B8B",
          },
        },
      },
    },
  },
});

export default theme;